/// <amd-module name="Wit/Messages/Components/BoardsMessages" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { equals } from "VSS/Core/Util/String";
import { VssComponent } from "VSS/Platform/Layout";
import { Link } from "VSSUI/Link";
import { IListItemDetails, ListItem, ScrollableList } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { ArrayItemProvider } from "VSSUI/Utilities/Provider";
import * as Resources from "Wit/Messages/Resources";
import { IBoardsMessagesService } from "../BoardsMessagesService";
import { IBoardsMessage } from "../Contracts";

export interface IBoardsMessagesProps {
    /**
     * CSS class name to supply to the messages container
     */
    className?: string;

    /**
     * Will only render if the type of the message is in this array
     */
    typeFilter?: (type?: string) => boolean;

    /**
     * the name of this message container. If a message has a container name set, it will only be displayed in a container with the same name.
     */
    containerName?: string;
}

export module ContainerNames {
    export const BacklogTab = "BacklogTab";
    export const BoardTab = "BoardTab";
    export const TaskboardTab = "TaskboardTab";
}

export class BoardsMessages extends VssComponent<IBoardsMessagesProps> {
    private showFullDetailsToggle = new ObservableValue(false);
    public render() {
        const messagesService = this.context.pageContext.getService<IBoardsMessagesService>("IBoardsMessagesService");

        return (
            <Observer message={messagesService.getMessage()} detailsToggle={this.showFullDetailsToggle}>
                {(props: { message?: IBoardsMessage; detailsToggle: boolean }) => {
                    return props.message && this.messageIsAllowedToDisplay(props.message) ? (
                        <MessageCard
                            className={this.props.className}
                            severity={props.message.severity || MessageCardSeverity.Info}
                            onDismiss={() => messagesService.dismissMessage()}
                            buttonProps={props.message.buttons}
                        >
                            <div className="flex-column">
                                <div>
                                    {props.message.renderMessage()}
                                    &nbsp;
                                    {props.message.moreDetails && (
                                        <Link
                                            tooltipProps={{ overflowOnly: true }}
                                            onClick={() => (this.showFullDetailsToggle.value = !this.showFullDetailsToggle.value)}
                                        >
                                            {!props.detailsToggle ? Resources.ShowDetails : Resources.HideDetails}
                                        </Link>
                                    )}
                                </div>
                                {props.detailsToggle && (
                                    <ScrollableList
                                        className="padding-top-8"
                                        itemProvider={new ArrayItemProvider(props.message.moreDetails ?? [])}
                                        renderRow={this.renderRow}
                                        renderHeader={() => <div className="padding-top-4">{Resources.OneOrMoreErrors}</div>}
                                    ></ScrollableList>
                                )}
                            </div>
                        </MessageCard>
                    ) : null;
                }}
            </Observer>
        );
    }

    private messageIsAllowedToDisplay = (message: IBoardsMessage): boolean => {
        const { typeFilter } = this.props;
        const messageTypeIsMatchingFilter = typeFilter ? typeFilter(message.type) : true;
        const boardContainerNameIsSuitable = message.containerName ? equals(message.containerName, this.props.containerName ?? "", true) : true;
        return messageTypeIsMatchingFilter && boardContainerNameIsSuitable;
    };

    private renderRow = (index: number, item: string, details: IListItemDetails<string>): JSX.Element => {
        return (
            <ListItem index={index} details={details}>
                <div className="flex-row h-scroll-hidden padding-top-4">{item}</div>
            </ListItem>
        );
    };
}
